import type { TFunction } from 'i18next'

import type { QuoteDraft } from '@/features/quotes/quoteDraft'
import type { PricingReopenCause, QuoteDetail, QuotePricing } from '@/types/domain'

/* A ready price sent back to «Ждёт расчёта» by an edit (N-11, issue #132).
 *
 * The server decides everything here and the client only words it: which edit
 * reset the price (`pricing.reopenCauses`, stored by `quote/pricing.go`
 * PriceRelevantChanges) and whether THIS reader's edit would reset it
 * (`pricing.reopensOnEdit`, `quote.ReopensPricing` computed per reader). No
 * arithmetic, no role names, no capability checks — the flag is server data.
 */

type CauseKey =
  | 'quotes.pricingState.causes.cargo'
  | 'quotes.pricingState.causes.routeOption'
  | 'quotes.pricingState.causes.transportMode'
  | 'quotes.pricingState.causes.pickupLocation'
  | 'quotes.pricingState.causes.incoterm'
  | 'quotes.pricingState.causes.transitDays'
  | 'quotes.pricingState.causes.measures'
  | 'quotes.pricingState.causes.other'

const CAUSE_KEYS: Record<PricingReopenCause, CauseKey> = {
  cargo: 'quotes.pricingState.causes.cargo',
  'route-option': 'quotes.pricingState.causes.routeOption',
  'transport-mode': 'quotes.pricingState.causes.transportMode',
  'pickup-location': 'quotes.pricingState.causes.pickupLocation',
  incoterm: 'quotes.pricingState.causes.incoterm',
  'transit-days': 'quotes.pricingState.causes.transitDays',
  measures: 'quotes.pricingState.causes.measures',
}

/** The i18n key for a served cause code. A code this client does not know —
 *  a later server's — reads as «другое» rather than as a raw code. */
export function reopenCauseKey(code: string): CauseKey {
  return Object.hasOwn(CAUSE_KEYS, code)
    ? CAUSE_KEYS[code as PricingReopenCause]
    : 'quotes.pricingState.causes.other'
}

/** «Сброшен после правки: Инкотермс — Walk Sales, 27 окт., 17:42», or null
 *  when the quote was not reached by a reset — a requested quote someone asked
 *  about keeps its «Запрос: …» line. On a reset the served requestedAt /
 *  requestedByName are the reset's when and who. */
export function reopenedLine(
  pricing: QuotePricing,
  t: TFunction,
  format: { timestamp: (iso: string) => string },
): string | null {
  if (pricing.state !== 'requested' || !pricing.reopenCauses?.length) return null
  const causes = [...new Set(pricing.reopenCauses.map(reopenCauseKey))]
    .map((key) => t(key))
    .join(', ')
  const when = pricing.requestedAt ? format.timestamp(pricing.requestedAt) : t('common.empty')
  return pricing.requestedByName
    ? t('quotes.pricingState.reopenedBy', { causes, name: pricing.requestedByName, when })
    : t('quotes.pricingState.reopenedAt', { causes, when })
}

/** Whether saving `draft` on the «Условия» card would reset `record`'s ready
 *  price — asked BEFORE the save so the reader can confirm it.
 *
 *  Mirrors the Incoterm term of `quote/pricing.go` PriceRelevantChanges, the
 *  only price field that card asks about, compared after the server's trim
 *  (service.go `Change.trimmed`) so whitespace alone is no change. Whether the
 *  edit would reset at all for this reader is the server's `reopensOnEdit`. */
export function incotermSaveResetsPricing(
  record: Pick<QuoteDetail, 'pricing' | 'incoterm'>,
  draft: Pick<QuoteDraft, 'incoterm'>,
): boolean {
  return (
    record.pricing.state === 'ready' &&
    record.pricing.reopensOnEdit === true &&
    (draft.incoterm ?? '').trim() !== (record.incoterm ?? '').trim()
  )
}
